import React, { useState, useEffect, useRef } from 'react';
import {
    Card, Table, Button, Input, InputNumber, Space, Typography, message, Row, Col,
    Tag, Divider, Select, Modal, AutoComplete, Statistic
} from 'antd';
import {
    BarcodeOutlined, DeleteOutlined, ShoppingCartOutlined,
    PlusOutlined, ScanOutlined, UserOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import api from '../../services/api';

const { Title, Text } = Typography;

const Sell = () => {
    const { t } = useTranslation(['pharmacy', 'common']);
    const [cart, setCart] = useState([]);
    const [barcodeInput, setBarcodeInput] = useState('');
    const [patients, setPatients] = useState([]);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [patientSearch, setPatientSearch] = useState('');
    const [currency, setCurrency] = useState('IQD');
    const [paymentMethod, setPaymentMethod] = useState('Cash');
    const [discount, setDiscount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [lastScanned, setLastScanned] = useState(null);
    const barcodeRef = useRef(null);
    const [salesHistory, setSalesHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    useEffect(() => {
        fetchTodaySales();
        // Auto-focus barcode input
        if (barcodeRef.current) barcodeRef.current.focus();
    }, []);

    useEffect(() => {
        if (patientSearch.length >= 2) {
            searchPatients(patientSearch);
        }
    }, [patientSearch]);

    const searchPatients = async (search) => {
        try {
            const res = await api.get(`/patients?search=${search}&limit=10`);
            const data = res.data.data || res.data.patients || [];
            setPatients(data.map(p => ({
                value: p._id,
                label: `${p.name} ${p.patientId ? `(${p.patientId})` : ''}`,
                patient: p
            })));
        } catch (error) {
            console.error('Patient search error:', error);
        }
    };

    const fetchTodaySales = async () => {
        try {
            setHistoryLoading(true);
            const today = dayjs().format('YYYY-MM-DD');
            const res = await api.get(`/pharmacy/sales?startDate=${today}&endDate=${today}`);
            setSalesHistory(res.data.data || []);
        } catch (error) {
            console.error('Error fetching sales:', error);
        } finally {
            setHistoryLoading(false);
        }
    };

    const handleBarcodeScan = async (barcode) => {
        if (!barcode.trim()) return;

        try {
            const res = await api.get(`/pharmacy/items/barcode/${barcode.trim()}`);
            const item = res.data.data;

            if (item.quantity <= 0) {
                message.warning(t('pharmacy:messages.outOfStock', { item: item.itemName }));
                setBarcodeInput('');
                return;
            }

            // Check if item already in cart
            const existingIndex = cart.findIndex(c => c.pharmacyItemId === item._id);
            if (existingIndex >= 0) {
                const updated = [...cart];
                const existing = updated[existingIndex];
                if (existing.quantity + 1 > item.quantity) {
                    message.warning(t('pharmacy:messages.onlyAvailable', { count: item.quantity, item: item.itemName }));
                } else {
                    existing.quantity += 1;
                    existing.total = existing.quantity * existing.unitPrice - existing.discount;
                    setCart(updated);
                    message.success(`+1 ${item.itemName} (${existing.quantity} total)`);
                }
            } else {
                setCart([...cart, {
                    pharmacyItemId: item._id,
                    itemName: item.itemName,
                    barcode: item.barcode,
                    quantity: 1,
                    maxQuantity: item.quantity,
                    unitPrice: item.sellingPrice,
                    discount: 0,
                    total: item.sellingPrice,
                    currency: item.currency
                }]);
                // Auto-set currency from first item
                if (cart.length === 0) setCurrency(item.currency);
                message.success(`Added: ${item.itemName}`);
            }

            setLastScanned(item);
            setBarcodeInput('');
        } catch (error) {
            if (error.response?.status === 404) {
                message.error(t('pharmacy:messages.itemNotFound'));
            } else {
                message.error(t('pharmacy:messages.scanError'));
            }
            setBarcodeInput('');
        }

        // Re-focus barcode input
        setTimeout(() => barcodeRef.current?.focus(), 100);
    };

    const updateCartItem = (index, field, value) => {
        const updated = [...cart];
        updated[index][field] = value;
        updated[index].total = updated[index].quantity * updated[index].unitPrice - updated[index].discount;
        setCart(updated);
    };

    const removeCartItem = (index) => {
        setCart(cart.filter((_, i) => i !== index));
    };

    const subtotal = cart.reduce((sum, item) => sum + item.total, 0);
    const totalAmount = subtotal - discount;

    const handleCompleteSale = async () => {
        if (cart.length === 0) {
            message.error(t('pharmacy:messages.cartEmpty'));
            return;
        }

        try {
            setLoading(true);
            const saleData = {
                ...(selectedPatient && { patientId: selectedPatient }),
                items: cart.map(item => ({
                    pharmacyItemId: item.pharmacyItemId,
                    itemName: item.itemName,
                    quantity: item.quantity,
                    unitPrice: item.unitPrice,
                    discount: item.discount
                })),
                subtotal,
                discount,
                tax: 0,
                totalAmount,
                amountPaid: totalAmount,
                currency,
                paymentMethod,
                paymentStatus: 'Paid'
            };

            await api.post('/pharmacy/sales', saleData);
            message.success(t('pharmacy:messages.saleCompleted'));

            // Reset
            setCart([]);
            setSelectedPatient(null);
            setPatientSearch('');
            setDiscount(0);
            setLastScanned(null);
            fetchTodaySales();

            // Refocus barcode
            setTimeout(() => barcodeRef.current?.focus(), 100);
        } catch (error) {
            message.error(error.response?.data?.message || t('pharmacy:messages.saleFailed'));
        } finally {
            setLoading(false);
        }
    };

    const cartColumns = [
        {
            title: t('pharmacy:common.number'),
            render: (_, __, index) => index + 1,
            width: 40
        },
        {
            title: t('pharmacy:common.item'),
            dataIndex: 'itemName',
            key: 'itemName',
            render: (text, record) => (
                <div>
                    <Text strong>{text}</Text>
                    {record.barcode && <div><Text type="secondary" style={{ fontSize: 11 }}><BarcodeOutlined /> {record.barcode}</Text></div>}
                </div>
            )
        },
        {
            title: t('pharmacy:sell.price'),
            dataIndex: 'unitPrice',
            key: 'unitPrice',
            width: 120,
            render: (price) => currency === 'IQD' ? `IQD ${price.toLocaleString()}` : `$${price.toFixed(2)}`
        },
        {
            title: t('pharmacy:common.qty'),
            dataIndex: 'quantity',
            key: 'quantity',
            width: 90,
            render: (qty, record, index) => (
                <InputNumber
                    min={1}
                    max={record.maxQuantity}
                    value={qty}
                    size="small"
                    onChange={(val) => updateCartItem(index, 'quantity', val)}
                />
            )
        },
        {
            title: t('pharmacy:common.discount'),
            dataIndex: 'discount',
            key: 'discount',
            width: 100,
            render: (disc, _, index) => (
                <InputNumber
                    min={0}
                    value={disc}
                    size="small"
                    onChange={(val) => updateCartItem(index, 'discount', val || 0)}
                />
            )
        },
        {
            title: t('pharmacy:common.total'),
            dataIndex: 'total',
            key: 'total',
            width: 120,
            render: (total) => (
                <Text strong>
                    {currency === 'IQD' ? `IQD ${total.toLocaleString()}` : `$${total.toFixed(2)}`}
                </Text>
            )
        },
        {
            title: '',
            key: 'action',
            width: 50,
            render: (_, __, index) => (
                <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeCartItem(index)} />
            )
        }
    ];

    const salesColumns = [
        { title: t('pharmacy:sell.saleNumber'), dataIndex: 'saleNumber', key: 'saleNumber' },
        {
            title: t('pharmacy:common.patient'),
            dataIndex: 'patientId',
            key: 'patient',
            render: (p) => p?.name || '-'
        },
        {
            title: t('pharmacy:common.items'),
            dataIndex: 'items',
            key: 'items',
            render: (items) => items?.length || 0
        },
        {
            title: t('pharmacy:common.total'),
            dataIndex: 'totalAmount',
            key: 'total',
            render: (amount, record) => record.currency === 'IQD' ? `IQD ${amount.toLocaleString()}` : `$${amount.toFixed(2)}`
        },
        {
            title: t('pharmacy:common.status'),
            dataIndex: 'paymentStatus',
            key: 'status',
            render: (status) => (
                <Tag color={status === 'Paid' ? 'green' : status === 'Partial' ? 'orange' : 'red'}>
                    {t(`pharmacy:common.paymentStatuses.${status}`, status)}
                </Tag>
            )
        },
        {
            title: t('pharmacy:sell.time'),
            dataIndex: 'saleDate',
            key: 'time',
            render: (date) => dayjs(date).format('HH:mm')
        }
    ];

    return (
        <div style={{ padding: 24 }}>
            <Title level={2}><ShoppingCartOutlined /> {t('pharmacy:sell.title')}</Title>

            <Row gutter={24}>
                {/* Left: Scanner & Cart */}
                <Col xs={24} lg={16}>
                    {/* Barcode Scanner */}
                    <Card
                        style={{ marginBottom: 16, border: '2px solid #1890ff' }}
                        bodyStyle={{ padding: '16px 24px' }}
                    >
                        <Row align="middle" gutter={16}>
                            <Col flex="auto">
                                <Input
                                    ref={barcodeRef}
                                    size="large"
                                    prefix={<ScanOutlined style={{ color: '#1890ff', fontSize: 20 }} />}
                                    placeholder={t('pharmacy:sell.scanPlaceholder')}
                                    value={barcodeInput}
                                    onChange={(e) => setBarcodeInput(e.target.value)}
                                    onPressEnter={() => handleBarcodeScan(barcodeInput)}
                                    autoFocus
                                    style={{ fontSize: 18 }}
                                />
                            </Col>
                            <Col>
                                <Button
                                    type="primary"
                                    size="large"
                                    icon={<BarcodeOutlined />}
                                    onClick={() => handleBarcodeScan(barcodeInput)}
                                >
                                    {t('pharmacy:sell.add')}
                                </Button>
                            </Col>
                        </Row>
                        {lastScanned && (
                            <div style={{ marginTop: 8 }}>
                                <Text type="secondary">
                                    {t('pharmacy:sell.lastScanned')} <Text strong>{lastScanned.itemName}</Text> — {t('pharmacy:sell.stockLabel')} {lastScanned.quantity}
                                </Text>
                            </div>
                        )}
                    </Card>

                    {/* Cart Table */}
                    <Card title={t('pharmacy:sell.cartTitle', { count: cart.length })} style={{ marginBottom: 16 }}>
                        <Table
                            dataSource={cart}
                            columns={cartColumns}
                            rowKey="pharmacyItemId"
                            pagination={false}
                            size="small"
                            locale={{ emptyText: t('pharmacy:sell.cartEmpty') }}
                        />
                    </Card>

                    {/* Today's Sales */}
                    <Card title={t('pharmacy:sell.todaysSales')} size="small">
                        <Table
                            dataSource={salesHistory}
                            columns={salesColumns}
                            rowKey="_id"
                            size="small"
                            loading={historyLoading}
                            pagination={{ pageSize: 5 }}
                            locale={{ emptyText: t('pharmacy:sell.noSalesToday') }}
                        />
                    </Card>
                </Col>

                {/* Right: Payment Panel */}
                <Col xs={24} lg={8}>
                    <Card title={t('pharmacy:sell.saleDetails')} style={{ position: 'sticky', top: 80 }}>
                        {/* Patient Selection */}
                        <div style={{ marginBottom: 16 }}>
                            <Text strong style={{ display: 'block', marginBottom: 4 }}>{t('pharmacy:sell.patientOptional')}</Text>
                            <AutoComplete
                                style={{ width: '100%' }}
                                options={patients}
                                value={patientSearch}
                                onSearch={setPatientSearch}
                                onSelect={(value, option) => {
                                    setSelectedPatient(value);
                                    setPatientSearch(option.label);
                                }}
                                placeholder={t('pharmacy:sell.searchPatient')}
                            >
                                <Input prefix={<UserOutlined />} />
                            </AutoComplete>
                        </div>

                        {/* Currency */}
                        <div style={{ marginBottom: 16 }}>
                            <Text strong style={{ display: 'block', marginBottom: 4 }}>{t('pharmacy:common.currency')}</Text>
                            <Select value={currency} onChange={setCurrency} style={{ width: '100%' }}>
                                <Select.Option value="IQD">{t('pharmacy:common.iqdFull')}</Select.Option>
                                <Select.Option value="USD">{t('pharmacy:common.usdFull')}</Select.Option>
                            </Select>
                        </div>

                        {/* Payment Method */}
                        <div style={{ marginBottom: 16 }}>
                            <Text strong style={{ display: 'block', marginBottom: 4 }}>{t('pharmacy:sell.paymentMethod')}</Text>
                            <Select value={paymentMethod} onChange={setPaymentMethod} style={{ width: '100%' }}>
                                <Select.Option value="Cash">{t('pharmacy:common.paymentMethods.Cash')}</Select.Option>
                                <Select.Option value="Credit Card">{t('pharmacy:common.paymentMethods.Credit Card')}</Select.Option>
                                <Select.Option value="Debit Card">{t('pharmacy:common.paymentMethods.Debit Card')}</Select.Option>
                                <Select.Option value="Bank Transfer">{t('pharmacy:common.paymentMethods.Bank Transfer')}</Select.Option>
                                <Select.Option value="Digital Wallet">{t('pharmacy:common.paymentMethods.Digital Wallet')}</Select.Option>
                            </Select>
                        </div>

                        <Divider />

                        {/* Totals */}
                        <div style={{ marginBottom: 12 }}>
                            <Row justify="space-between">
                                <Text>{t('pharmacy:sell.subtotal')}</Text>
                                <Text strong>{currency === 'IQD' ? `IQD ${subtotal.toLocaleString()}` : `$${subtotal.toFixed(2)}`}</Text>
                            </Row>
                        </div>

                        <div style={{ marginBottom: 12 }}>
                            <Row justify="space-between" align="middle">
                                <Text>{t('pharmacy:sell.discountLabel')}</Text>
                                <InputNumber
                                    min={0}
                                    max={subtotal}
                                    value={discount}
                                    onChange={(val) => setDiscount(val || 0)}
                                    style={{ width: 120 }}
                                    size="small"
                                />
                            </Row>
                        </div>

                        <Divider style={{ margin: '8px 0' }} />

                        <div style={{ marginBottom: 12 }}>
                            <Row justify="space-between">
                                <Text strong style={{ fontSize: 16 }}>{t('pharmacy:sell.totalLabel')}</Text>
                                <Text strong style={{ fontSize: 18, color: '#1890ff' }}>
                                    {currency === 'IQD' ? `IQD ${totalAmount.toLocaleString()}` : `$${totalAmount.toFixed(2)}`}
                                </Text>
                            </Row>
                        </div>

                        <Button
                            type="primary"
                            size="large"
                            block
                            icon={<ShoppingCartOutlined />}
                            onClick={handleCompleteSale}
                            loading={loading}
                            disabled={cart.length === 0}
                            style={{ marginTop: 16, height: 50, fontSize: 16 }}
                        >
                            {t('pharmacy:sell.completeSale')}
                        </Button>

                        <Button
                            block
                            style={{ marginTop: 8 }}
                            onClick={() => {
                                setCart([]);
                                setDiscount(0);
                                setLastScanned(null);
                            }}
                            disabled={cart.length === 0}
                        >
                            {t('pharmacy:sell.clearCart')}
                        </Button>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default Sell;
